import React, { useContext, useEffect, useRef, useState } from "react";
import { SearchContext } from "../context/HotelRoomSearch";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

export default function HotelSearchBar() {
  // Normal Variables
  const navigate = useNavigate();
  const checkinDef = dayjs();
  const checkoutDef = checkinDef.add(4, "day");

  // Context
  const { searchValues, setSearchValues } = useContext(SearchContext);

  // State Handling
  const [openCounterBox, setOpenCounterBox] = useState(false);

  // Refs
  const counterBoxRef = useRef(null);

  // Clicking Outside the counter box to close it
  const handleClickOutside = (e) => {
    if (counterBoxRef.current && !counterBoxRef.current.contains(e.target)) {
      setOpenCounterBox(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
  }, []);

  // Opening the Counter Box
  const toggleCounterBox = () => {
    setOpenCounterBox(true);
  };

  const focusHandler = (e) => {
    e.stopPropagation();
    toggleCounterBox();
  };

  // Date Range Picker From Ant Design
  const { RangePicker } = DatePicker;
  const dateFormat = "YYYY-MM-DD";

  const settingCheckinCheckout = (dates) => {
    if(dates){
      setSearchValues({
        ...searchValues,
        checkin: `${dates[0].$y}-${dates[0].$M + 1}-${dates[0].$D}`,
        checkout: `${dates[1].$y}-${dates[1].$M + 1}-${dates[1].$D}`,
      });
    } else {
      setSearchValues({
        ...searchValues,
        checkin: `${checkinDef.$y}-${checkinDef.$M + 1}-${checkinDef.$D}`,
        checkout: `${checkoutDef.$y}-${checkoutDef.$M + 1}-${checkoutDef.$D}`,
     });
    };
  }

  // searchHotelRoom
  const searchHotelRoom = () => {
    const path = window.location.pathname;
    if (path == "/book-now") {
      return;
    } else {
      navigate("/book-now");
    }
  };

  return (
    <>
      <div className="container-fluid p-0 hotel-search-bar">
        <div className="row app-container">
          <div className="col-12 p-0 hotel-search-bar-fields">
            <div className="d-flex flex-wrap gap-md-2 gap-3">
              {/* Where To Search Field Start */}
              <div className="col-lg col-12 single-search-field whereto-location d-flex justify-content-start align-items-stretch position-relative">
                <div className="search-field-icon">
                  <i className="fa-solid fa-location-dot"></i>
                </div>
                <input
                  type="text"
                  className="form-control text-capitalize"
                  placeholder="Where to?"
                  required
                  value={searchValues.location}
                  onChange={(e) => {
                    setSearchValues({
                      ...searchValues,
                      location: e.target.value.toLowerCase(),
                    });
                  }}
                />
                <p className="m-0 p-0 location-guide d-flex justify-content-lg-center justify-content-start">Use Spaces as Seperation</p>
              </div>
              {/* Where To Search Field End */}

              {/* Dates Input Field Start */}
              <div className="col-lg col-12 single-search-field d-flex justify-content-start align-items-stretch">
                <div className="search-field-icon">
                  <i className="fa-solid fa-calendar-days"></i>
                </div>
                <div className="p-0 m-0 w-100 position-relative dates">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Dates"
                    required
                    value={
                      searchValues.checkin && searchValues.checkout
                        ? `${searchValues.checkin} to ${
                            searchValues.checkout
                          }`
                        : ""
                    }
                  />
                  {searchValues.checkin && searchValues.checkout ? (
                    <RangePicker
                      className="rangePicker"
                      placeholder={["Check in", "Check out"]}
                      defaultValue={[
                        dayjs(searchValues.checkin),
                        dayjs(searchValues.checkout)
                      ]}
                      format={dateFormat}
                      onChange={(dates) => {
                        settingCheckinCheckout(dates);
                      }}
                    />
                  ) : (
                    ""
                  )}
                </div>
              </div>
              {/* Dates Input Field End */}

              {/* How Many People and Rooms Start */}
              <div className="col-sm col-12 single-search-field d-flex justify-content-start align-items-stretch position-relative">
                <div className="search-field-icon">
                  <i className="fa-solid fa-user"></i>
                </div>
                <input
                  type="text"
                  className="form-control"
                  value={`${
                    (searchValues?.counter?.adults
                      ? searchValues.counter.adults
                      : 2) +
                    (searchValues?.counter?.children
                      ? searchValues.counter.children
                      : 0)
                  } Guests, ${
                    searchValues?.counter?.rooms
                      ? searchValues.counter.rooms
                      : 1
                  } Rooms`}
                  readOnly
                  onClick={focusHandler}
                />

                {/* Counter Start */}
                {openCounterBox && (
                  <div className="peopleAndRoomCounter" ref={counterBoxRef}>
                    {/* Rooms */}
                    <div className="howMany d-flex justify-content-between align-items-center">
                      <p className="p-0 m-0">Rooms</p>
                      <div className="counter d-flex align-items-center">
                        <button
                          className="btn minus"
                          onClick={() => {
                            setSearchValues({
                              ...searchValues,
                              counter: {
                                ...searchValues.counter,
                                rooms:
                                  searchValues.counter.rooms > 1
                                    ? searchValues.counter.rooms - 1
                                    : 1,
                              },
                            });
                          }}
                        >
                          -
                        </button>
                        <span className="counterValue fw-medium">
                          {searchValues.counter.rooms}
                        </span>
                        <button
                          className="btn plus"
                          onClick={() => {
                            setSearchValues({
                              ...searchValues,
                              counter: {
                                ...searchValues.counter,
                                rooms:
                                  searchValues.counter.rooms < 12
                                    ? searchValues.counter.rooms + 1
                                    : 12,
                              },
                            });
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Adults */}
                    <div className="howMany d-flex justify-content-between align-items-center">
                      <p className="p-0 m-0">Adults</p>
                      <div className="counter d-flex align-items-center">
                        <button
                          className="btn minus"
                          onClick={() => {
                            setSearchValues({
                              ...searchValues,
                              counter: {
                                ...searchValues.counter,
                                adults:
                                  searchValues.counter.adults > 1
                                    ? searchValues.counter.adults - 1
                                    : 1,
                              },
                            });
                          }}
                        >
                          -
                        </button>
                        <span className="counterValue fw-medium">
                          {searchValues.counter.adults}
                        </span>
                        <button
                          className="btn plus"
                          onClick={() => {
                            setSearchValues({
                              ...searchValues,
                              counter: {
                                ...searchValues.counter,
                                adults:
                                  searchValues.counter.adults < 12
                                    ? searchValues.counter.adults + 1
                                    : 12,
                              },
                            });
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Childrens */}
                    <div className="howMany d-flex justify-content-between align-items-center">
                      <p className="p-0 m-0">Childrens</p>
                      <div className="counter d-flex align-items-center">
                        <button
                          className="btn minus"
                          onClick={() => {
                            setSearchValues({
                              ...searchValues,
                              counter: {
                                ...searchValues.counter,
                                children:
                                  searchValues.counter.children > 1
                                    ? searchValues.counter.children - 1
                                    : 0,
                              },
                            });
                          }}
                        >
                          -
                        </button>
                        <span className="counterValue fw-medium">
                          {searchValues.counter.children}
                        </span>
                        <button
                          className="btn plus"
                          onClick={() => {
                            setSearchValues({
                              ...searchValues,
                              counter: {
                                ...searchValues.counter,
                                children:
                                  searchValues.counter.children < 12
                                    ? searchValues.counter.children + 1
                                    : 12,
                              },
                            });
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {/* Counter End */}
              </div>
              {/* How Many People and Rooms End */}

              {/* Search Button Start */}
              <div className="col-sm-auto col-12 px-2 search-bar-search-btn d-flex justify-content-center align-items-center">
                <button
                  className="btn custom-btn rounded-pill"
                  onClick={searchHotelRoom}
                >
                  Search
                </button>
              </div>
              {/* Search Button End */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
